import Vue from 'vue'
import Router from 'vue-router'
import auth from '@/auth/authService'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      // =============================================================================
      // MAIN ROUTES
      // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '/',
          redirect: '/dashboard',
        },
        {
          path: '/dashboard',
          name: 'dashboard',
          component: () =>
            import('./views/apps/dashboard/ArAutomationDashboard.vue'),
          meta: {
            pageTitle: 'AR AUTOMATION DASHBOARD',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/EInvoiceDashboard',
          name: 'EInvoiceDashboard',
          component: () =>
            import('./views/apps/dashboard-2/EInvoiceDashboard.vue'),
          meta: {
            pageTitle: 'E INVOICE DASHBOARD',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/email-logs-report',
          name: 'Email Logs Report',
          component: () =>
            import('./views/apps/reporting-dashboard/EmailLogsReport.vue'),
          meta: {
            pageTitle: 'Email Delivery',
            i18nKey: 'nav.rptEmailDelivery',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/Reconciliation-Report',
          name: 'Reconciliation Report',
          component: () =>
            import('./views/apps/reporting-dashboard/ReconciliationReport.vue'),
          meta: {
            pageTitle: 'Reconciliation',
            i18nKey: 'nav.rptReconciliation',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/eDocument-by-status-report',
          name: 'eDocument By Status Report',
          component: () =>
            import(
              './views/apps/reporting-dashboard/eDocumentByStatusReport.vue'
            ),
          meta: {
            pageTitle: 'e-Invoice Status',
            i18nKey: 'nav.rptEInvoiceStatus',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/proof-of-income-validation-report',
          name: 'Proof of Income Validation Report',
          component: () =>
            import(
              './views/apps/reporting-dashboard/ProofOfIncomeValidationReport.vue'
            ),
          meta: {
            pageTitle: 'Validated Sales',
            i18nKey: 'nav.rptValidatedSales',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/proof-of-expenses-validation-report',
          name: 'Proof of Expenses Validation Report',
          component: () =>
            import(
              './views/apps/reporting-dashboard/ProofOfExpensesValidationReport.vue'
            ),
          meta: {
            pageTitle: 'Validated Purchases',
            i18nKey: 'nav.rptValidatedPurchases',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/proof-of-income-monthly-report',
          name: 'Proof of Income Monthly Report',
          component: () =>
            import(
              './views/apps/reporting-dashboard/ProofOfIncomeMonthlyReport.vue'
            ),
          meta: {
            pageTitle: 'Monthly Sales',
            i18nKey: 'nav.rptMonthlySales',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/ap-documents-report',
          name: 'AP Documents Report',
          component: () =>
            import('./views/apps/advanced-reporting/ApDocumentsReport.vue'),
          meta: {
            pageTitle: 'Supplier e-Invoices',
            i18nKey: 'nav.rptSupplierEInvoices',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/ap-documents',
          name: 'ap-documents',
          component: () =>
            import('./views/apps/ap-documents/ApDocumentList.vue'),
          meta: {
            pageTitle: 'Supplier e-Invoices (LHDN)',
            i18nKey: 'nav.supplierEInvoicesLhdn',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          // Purchases > Expense Doc — plain per-type received-document lists
          // (the buyer-side mirror of Sales > Billing Doc).
          path: '/expense-docs/type/:type',
          name: 'expense-docs-by-type',
          component: () =>
            import('./views/apps/expense-docs/ExpenseDocList.vue'),
          meta: {
            rule: 'admin',
            authRequired: true,
          },
          beforeEnter: (to, from, next) => {
            const titles = {
              invoice: 'Supplier Invoices',
              'credit-note': 'Supplier Credit Notes',
              'debit-note': 'Supplier Debit Notes',
              'refund-note': 'Supplier Refund Notes',
            }
            to.meta.pageTitle = titles[to.params.type] || 'Supplier Invoices'
            next()
          },
        },
        {
          path: '/expense-docs/:id',
          name: 'expense-doc-detail',
          component: () =>
            import('./views/apps/expense-docs/ExpenseDocDetail.vue'),
          meta: {
            pageTitle: 'Supplier Document',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Purchases', url: '/expense-docs/type/invoice' },
              { title: 'Display', active: true },
            ],
          },
        },
        {
          path: '/ap-documents/:uuid',
          name: 'ap-document-detail',
          component: () =>
            import('./views/apps/ap-documents/ApDocumentDetail.vue'),
          meta: {
            pageTitle: 'AP DOCUMENT',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'AP Documents', url: '/ap-documents' },
              { title: 'Detail', active: true },
            ],
          },
        },
        {
          path: '/document-ledger-report',
          name: 'Document Ledger Report',
          component: () =>
            import('./views/apps/advanced-reporting/DocumentLedgerReport.vue'),
          meta: {
            pageTitle: 'All Documents',
            i18nKey: 'nav.rptAllDocuments',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/csv-import-guide',
          name: 'CsvImportGuide',
          component: () =>
            import('./views/apps/csv-import-guide/CsvImportGuide.vue'),
          meta: {
            pageTitle: 'CSV IMPORT GUIDE',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/customers',
          name: 'customers',
          component: () => import('./views/apps/customers/CustomerList.vue'),
          meta: {
            pageTitle: 'Customers',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/customers/import',
          name: 'customers/import',
          component: () =>
            import('./views/apps/customers/CustomerImportForm.vue'),
          meta: {
            pageTitle: 'Import Customers',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Customers', url: '/customers' },
              { title: 'Import', active: true },
            ],
          },
        },
        {
          path: '/customers/:customerId',
          name: 'customer-detail',
          component: () => import('./views/apps/customers/CustomerForm.vue'),
          meta: {
            pageTitle: 'Customer Detail',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Customers', url: '/customers' },
              { title: 'Detail', active: true },
            ],
          },
        },
        {
          path: '/customer-groups',
          name: 'customer-groups',
          component: () =>
            import('./views/apps/customer-groups/CustomerGroupList.vue'),
          meta: {
            pageTitle: 'Customers Groups',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/customer-groups/new',
          name: 'customer-groups/new',
          component: () =>
            import(
              './views/apps/customer-groups/CustomerGroupCreationForm.vue'
            ),
          meta: {
            pageTitle: 'New Customers Groups',
            rule: 'admin',
            authRequired: true,
          },
        },
        // {
        //   path: '/customer-groups/import',
        //   name: 'customer-groups/import',
        //   component: () =>
        //     import('./views/apps/customer-groups/CustomerGroupImportForm.vue'),
        //   meta: {
        //     pageTitle: 'Import Customer Groups Customers',
        //     rule: 'admin',
        //     authRequired: true,
        //     breadcrumb: [
        //       { title: 'Dashboard', url: '/dashboard' },
        //       { title: 'Customer Groups', url: '/customer-groups' },
        //       { title: 'Import', active: true },
        //     ],
        //   },
        // },
        {
          path: '/customer-groups/:groupId',
          name: 'customer-group-detail',
          component: () =>
            import('./views/apps/customer-groups/CustomerGroupForm.vue'),
          meta: {
            pageTitle: 'Customer Group Detail',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Customer Groups', url: '/customer-groups' },
              { title: 'Detail', active: true },
            ],
          },
        },
        {
          path: '/emailLogs',
          name: 'email-logs',
          component: () => import('./views/apps/email-logs/EmailLogList.vue'),
          meta: {
            pageTitle: 'Email Logs',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/emailLogs/:id',
          name: 'email-log-details',
          component: () => import('./views/apps/email-logs/EmailLogForm.vue'),
          meta: {
            pageTitle: 'Display Log',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Email Logs', url: '/emailLogs' },
              { title: 'Display Log', active: true },
            ],
          },
        },
        {
          path: '/files',
          name: 'files',
          component: () => import('./views/apps/file/FileList.vue'),
          meta: {
            pageTitle: 'Imports',
            i18nKey: 'nav.imports',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/files/:fileId',
          name: 'file-detail',
          component: () => import('./views/apps/file/FileForm.vue'),
          meta: {
            pageTitle: 'Display File',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Files', url: '/files' },
              { title: 'Display File', active: true },
            ],
          },
        },
        {
          path: '/invoices',
          name: 'invoices',
          component: () => import('./views/apps/invoices/InvoiceList.vue'),
          meta: {
            pageTitle: 'Invoices',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/invoices/customer/:customerId',
          name: 'customer-invoices',
          component: () => import('./views/apps/invoices/InvoiceList.vue'),
          meta: {
            pageTitle: 'Invoices',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/invoices/import',
          name: 'invoices/import',
          component: () =>
            import('./views/apps/invoices/InvoiceImportForm.vue'),
          meta: {
            pageTitle: 'Import Invoices',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Invoices', url: '/invoices' },
              { title: 'Import', active: true },
            ],
          },
        },
        {
          path: '/invoices/:docNo',
          name: 'invoice-detail',
          component: () => import('./views/apps/invoices/InvoiceForm.vue'),
          meta: {
            pageTitle: 'Display Invoice',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Invoices', url: '/invoices' },
              { title: 'Display Invoice', active: true },
            ],
          },
        },
        {
          path: '/pro-forma-invoices',
          name: 'pro-forma-invoices',
          component: () =>
            import('./views/apps/pro-forma-invoices/ProformaList.vue'),
          meta: {
            pageTitle: 'Pro Forma Invoices',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/pro-forma-invoices/import',
          name: 'pro-forma-invoices/import',
          component: () =>
            import('./views/apps/pro-forma-invoices/ProFormaImportForm.vue'),
          meta: {
            pageTitle: 'Import Pro Forma Invoices',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Pro Forma Invoices', url: '/pro-forma-invoices' },
              { title: 'Import', active: true },
            ],
          },
        },
        {
          path: '/pro-forma-invoices/:docNo',
          name: 'pro-forma-invoices-detail',
          component: () =>
            import('./views/apps/pro-forma-invoices/ProFormaForm.vue'),
          meta: {
            pageTitle: 'Display Pro Forma Invoice',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Pro Forma Invoices', url: '/pro-forma-invoices' },
              { title: 'Display', active: true },
            ],
          },
        },
        {
          path: '/credit-notes',
          name: 'credit-notes',
          component: () => import('./views/apps/credit-notes/CnList.vue'),
          meta: {
            pageTitle: 'Credit Notes',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/credit-notes/import',
          name: 'credit-notes/import',
          component: () => import('./views/apps/credit-notes/CnImportForm.vue'),
          meta: {
            pageTitle: 'Import Credit Notes',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Credit Notes', url: '/credit-notes' },
              { title: 'Import', active: true },
            ],
          },
        },
        {
          path: '/credit-notes/apply-invoice',
          name: 'credit-notes/apply-invoice',
          component: () =>
            import('./views/apps/credit-notes/CnImportApplyInvoiceForm.vue'),
          meta: {
            pageTitle: 'Apply Credit Notes to Invoice',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Credit Notes', url: '/credit-notes' },
              { title: 'Apply Invoice', active: true },
            ],
          },
        },
        {
          path: '/credit-notes/:docNo',
          name: 'credit-note-detail',
          component: () => import('./views/apps/credit-notes/CnForm.vue'),
          meta: {
            pageTitle: 'Display Credit Note',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Credit Notes', url: '/credit-notes' },
              { title: 'Display', active: true },
            ],
          },
        },
        {
          path: '/debit-notes',
          name: 'debit-notes',
          component: () => import('./views/apps/debit-notes/DnList.vue'),
          meta: {
            pageTitle: 'Debit Notes',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/debit-notes/import',
          name: 'debit-notes/import',
          component: () => import('./views/apps/debit-notes/DnImportForm.vue'),
          meta: {
            pageTitle: 'Import Debit Notes',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Debit Notes', url: '/debit-notes' },
              { title: 'Import', active: true },
            ],
          },
        },
        {
          path: '/debit-notes/apply-invoice',
          name: 'debit-notes/apply-invoice',
          component: () =>
            import('./views/apps/debit-notes/DnImportApplyInvoiceForm.vue'),
          meta: {
            pageTitle: 'Apply Debit Notes to Invoice',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Debit Notes', url: '/debit-notes' },
              { title: 'Apply Invoice', active: true },
            ],
          },
        },
        {
          path: '/debit-notes/:docNo',
          name: 'debit-note-detail',
          component: () => import('./views/apps/debit-notes/DnForm.vue'),
          meta: {
            pageTitle: 'Display Debit Notes',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Debit Notes', url: '/debit-notes' },
              { title: 'Display', active: true },
            ],
          },
        },
        {
          path: '/refund-notes',
          name: 'refund-notes',
          component: () =>
            import('./views/apps/refund-notes/RefundNoteList.vue'),
          meta: {
            pageTitle: 'Refund Notes',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/refund-notes/import',
          name: 'refund-notes/import',
          component: () =>
            import('./views/apps/refund-notes/RefundNoteImportForm.vue'),
          meta: {
            pageTitle: 'Import Refund Notes',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Refund Note', url: '/refund-notes' },
              { title: 'Import', active: true },
            ],
          },
        },
        {
          path: '/refund-notes/apply-invoice',
          name: 'refund-notes/apply-invoice',
          component: () =>
            import('./views/apps/refund-notes/RnImportApplyInvoiceForm.vue'),
          meta: {
            pageTitle: 'Apply Refund Notes to Invoice',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Refund Notes', url: '/refund-notes' },
              { title: 'Apply Invoice', active: true },
            ],
          },
        },
        {
          path: '/refund-notes/:docNo',
          name: 'refund-note-detail',
          component: () =>
            import('./views/apps/refund-notes/RefundNoteForm.vue'),
          meta: {
            pageTitle: 'Display Refund Note',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Refund Note', url: '/refund-notes' },
              { title: 'Display', active: true },
            ],
          },
        },
        {
          path: '/self-billed/type/:type',
          name: 'self-billed-documents',
          component: () =>
            import('./views/apps/self-billed/SelfBilledList.vue'),
          meta: {
            rule: 'admin',
            authRequired: true,
          },
          beforeEnter: (to, from, next) => {
            const type = to.params.type
            // Define titles based on type
            const titles = {
              invoice: 'Self Billed Invoices',
              'credit-note': 'Self Billed Credit Notes',
              'debit-note': 'Self Billed Debit Notes',
              'refund-note': 'Self Billed Refund Notes',
            }

            // Default title if type not found
            to.meta.pageTitle = titles[type] || 'Self Billed Documents'

            next()
          },
        },
        {
          path: '/self-billed/import',
          name: 'self-billed/import',
          component: () =>
            import('./views/apps/self-billed/SelfBilledImportForm.vue'),
          meta: {
            pageTitle: 'Import Self Billed Documents',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Import', active: true },
            ],
          },
        },
        {
          path: '/self-billed/:docNo',
          name: 'self-billed-document-detail',
          component: () =>
            import('./views/apps/self-billed/SelfBilledForm.vue'),
          meta: {
            pageTitle: 'Display Self Billed Document',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Display', active: true },
            ],
          },
        },
        {
          path: '/self-billed/type/credit-note/apply-invoice',
          name: 'self-billed-credit-note/apply-invoice',
          component: () =>
            import('./views/apps/self-billed/SbImportApplyInvoiceForm.vue'),
          meta: {
            pageTitle: 'Apply Self Billed Credit Note to Invoice',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              {
                title: 'Self Billed Credit Note',
                url: '/self-billed/type/credit-note',
              },
              { title: 'Apply Invoice', active: true },
            ],
          },
        },
        {
          path: '/self-billed/type/debit-note/apply-invoice',
          name: 'self-billed-debit-note/apply-invoice',
          component: () =>
            import('./views/apps/self-billed/SbImportApplyInvoiceForm.vue'),
          meta: {
            pageTitle: 'Apply Self Billed Debit Note to Invoice',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              {
                title: 'Self Billed Debit Note',
                url: '/self-billed/type/debit-note',
              },
              { title: 'Apply Invoice', active: true },
            ],
          },
        },
        {
          path: '/self-billed/type/refund-note/apply-invoice',
          name: 'self-billed-refund-note/apply-invoice',
          component: () =>
            import('./views/apps/self-billed/SbImportApplyInvoiceForm.vue'),
          meta: {
            pageTitle: 'Apply Self Billed Refund Note to Invoice',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              {
                title: 'Self Billed Refund Note',
                url: '/self-billed/type/refund-note',
              },
              { title: 'Apply Invoice', active: true },
            ],
          },
        },
        {
          path: '/consolidated',
          name: 'consolidated-invoices',
          component: () =>
            import('./views/apps/consolidated-invoices/ConsolidatedList.vue'),
          meta: {
            pageTitle: 'Consolidated Invoices',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/consolidated/import',
          name: 'consolidated-invoices/import',
          component: () =>
            import(
              './views/apps/consolidated-invoices/ConsolidatedImportForm.vue'
            ),
          meta: {
            pageTitle: 'Import Consolidated Invoices',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Consolidated Invoices', url: '/consolidated' },
              { title: 'Import', active: true },
            ],
          },
        },
        {
          path: '/consolidated/:docNo',
          name: 'consolidated-invoice-detail',
          component: () =>
            import('./views/apps/consolidated-invoices/ConsolidatedForm.vue'),
          meta: {
            pageTitle: 'Display Consolidated Invoice',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Consolidated Invoices', url: '/consolidated' },
              { title: 'Display', active: true },
            ],
          },
        },
        {
          path: '/e-invoices',
          name: 'e-invoices',
          component: () => import('./views/apps/e-invoices/EInvoiceList.vue'),
          meta: {
            pageTitle: 'E-Invoices',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/e-invoices/:billingDocNo',
          name: 'e-invoice-detail',
          component: () => import('./views/apps/e-invoices/EInvoiceForm.vue'),
          meta: {
            pageTitle: 'Display E-Invoice',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'E-Invoices', url: '/e-invoices' },
              { title: 'Display', active: true },
            ],
          },
        },
        {
          path: '/lhdn-submissions',
          name: 'lhdn-submissions',
          component: () =>
            import('./views/apps/lhdn-submissions/LhdnSubmissionsList.vue'),
          meta: {
            pageTitle: 'LHDN Submission History',
            i18nKey: 'nav.lhdnSubmissionHistory',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/lhdn-submissions/:uuid',
          name: 'lhdn-submission-detail',
          component: () =>
            import('./views/apps/lhdn-submissions/LhdnSubmissionsForm.vue'),
          meta: {
            pageTitle: 'Display Lhdn Submission',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Lhdn Submissions', url: '/lhdn-submissions' },
              { title: 'Display', active: true },
            ],
          },
        },
        {
          path: '/orders',
          name: 'orders',
          component: () => import('./views/apps/orders/OrderList.vue'),
          meta: {
            pageTitle: 'Orders',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/orders/import',
          name: 'orders/import',
          component: () =>
            import('./views/apps/orders/OrderImportForm.vue'),
          meta: {
            pageTitle: 'Import Orders',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Orders', url: '/orders' },
              { title: 'Import', active: true },
            ],
          },
        },
        {
          path: '/orders/:docNo',
          name: 'order-detail',
          component: () => import('./views/apps/orders/OrderForm.vue'),
          meta: {
            pageTitle: 'Display Order',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Orders', url: '/orders' },
              { title: 'Display Order', active: true },
            ],
          },
        },
        {
          path: '/audit-trail',
          name: 'audit-trail',
          component: () => import('./views/apps/audit-trail/AuditTrailList.vue'),
          meta: {
            pageTitle: 'Audit Trail',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Audit Trail', active: true },
            ],
          },
        },
        {
          path: '/sg-peppol-submissions',
          name: 'sg-peppol-submissions',
          component: () =>
            import('./views/apps/sg-peppol-submissions/SgPeppolSubmissionsList.vue'),
          meta: {
            pageTitle: 'Submission History',
            i18nKey: 'nav.submissionHistory',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/sg-peppol-submissions/:uuid',
          name: 'sg-peppol-submission-detail',
          component: () =>
            import('./views/apps/sg-peppol-submissions/SgPeppolSubmissionsForm.vue'),
          meta: {
            pageTitle: 'Display SG Peppol Submission',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'SG Peppol Submissions', url: '/sg-peppol-submissions' },
              { title: 'Display', active: true },
            ],
          },
        },
        {
          // Malaysia Peppol (MDEC) submission records — e-Invoice Exchange >
          // Peppol e-Invoice for hasMyPeppol tenants.
          path: '/my-peppol-submissions',
          name: 'my-peppol-submissions',
          component: () =>
            import('./views/apps/my-peppol-submissions/MyPeppolSubmissionsList.vue'),
          meta: {
            pageTitle: 'Peppol Submission History',
            i18nKey: 'nav.peppolSubmissionHistory',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/my-peppol-submissions/:uuid',
          name: 'my-peppol-submission-detail',
          component: () =>
            import('./views/apps/my-peppol-submissions/MyPeppolSubmissionsDetail.vue'),
          meta: {
            pageTitle: 'Peppol Submission',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Peppol Submissions', url: '/my-peppol-submissions' },
              { title: 'Display', active: true },
            ],
          },
        },
        {
          path: '/received-orders',
          name: 'received-orders',
          component: () =>
            import('./views/apps/peppol-inbound-orders/ReceivedOrdersList.vue'),
          meta: {
            pageTitle: 'Received Orders',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/received-orders/:id',
          name: 'received-order-detail',
          component: () =>
            import('./views/apps/peppol-inbound-orders/ReceivedOrderDetail.vue'),
          meta: {
            pageTitle: 'Received Order',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Received Orders', url: '/received-orders' },
              { title: 'Detail', active: true },
            ],
          },
        },
        {
          path: '/received-invoices',
          name: 'received-invoices',
          component: () =>
            import('./views/apps/peppol-ap-documents/ReceivedInvoicesList.vue'),
          meta: {
            pageTitle: 'Received Invoices',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/received-invoices/:id',
          name: 'received-invoice-detail',
          component: () =>
            import('./views/apps/peppol-ap-documents/ReceivedInvoiceDetail.vue'),
          meta: {
            pageTitle: 'Received Invoice',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Received Invoices', url: '/received-invoices' },
              { title: 'Detail', active: true },
            ],
          },
        },
        {
          path: '/PeppolDashboard',
          name: 'PeppolDashboard',
          component: () =>
            import('./views/apps/peppol-dashboard/PeppolDashboard.vue'),
          meta: {
            pageTitle: 'PEPPOL DASHBOARD',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/peppol-transmission-status-report',
          name: 'peppol-transmission-status-report',
          component: () =>
            import(
              './views/apps/peppol-reporting/PeppolTransmissionStatusReport.vue'
            ),
          meta: {
            pageTitle: 'Peppol Delivery Status',
            i18nKey: 'nav.rptPeppolDelivery',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/peppol-monthly-compliance-report',
          name: 'peppol-monthly-compliance-report',
          component: () =>
            import(
              './views/apps/peppol-reporting/PeppolMonthlyComplianceReport.vue'
            ),
          meta: {
            pageTitle: 'Peppol Monthly Compliance',
            i18nKey: 'nav.rptPeppolCompliance',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/peppol-reconciliation-report',
          name: 'peppol-reconciliation-report',
          component: () =>
            import(
              './views/apps/peppol-reporting/PeppolReconciliationReport.vue'
            ),
          meta: {
            pageTitle: 'Peppol Reconciliation',
            i18nKey: 'nav.rptPeppolReconciliation',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/my-peppol-dashboard',
          name: 'my-peppol-dashboard',
          component: () =>
            import('./views/apps/my-peppol-dashboard/MyPeppolDashboard.vue'),
          meta: {
            pageTitle: 'MY PEPPOL DASHBOARD',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/my-peppol-transmission-status-report',
          name: 'my-peppol-transmission-status-report',
          component: () =>
            import(
              './views/apps/my-peppol-reporting/MyPeppolTransmissionStatusReport.vue'
            ),
          meta: {
            pageTitle: 'Peppol Delivery Status',
            i18nKey: 'nav.rptPeppolDelivery',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/my-peppol-monthly-compliance-report',
          name: 'my-peppol-monthly-compliance-report',
          component: () =>
            import(
              './views/apps/my-peppol-reporting/MyPeppolMonthlyComplianceReport.vue'
            ),
          meta: {
            pageTitle: 'Peppol Monthly Compliance',
            i18nKey: 'nav.rptPeppolCompliance',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/my-peppol-reconciliation-report',
          name: 'my-peppol-reconciliation-report',
          component: () =>
            import(
              './views/apps/my-peppol-reporting/MyPeppolReconciliationReport.vue'
            ),
          meta: {
            pageTitle: 'Peppol Reconciliation',
            i18nKey: 'nav.rptPeppolReconciliation',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/payment-transactions',
          name: 'payment-transactions',
          component: () =>
            import(
              './views/apps/payment-transactions/PaymentTransactionList.vue'
            ),
          meta: {
            pageTitle: 'Payments Received',
            i18nKey: 'nav.paymentsReceived',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/payment-transactions/new/:customerId',
          name: 'payment-transactions/new',
          component: () =>
            import(
              './views/apps/payment-transactions/PaymentTransactionCreationForm.vue'
            ),
          meta: {
            pageTitle: 'New Payment',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Payments', url: '/payment-transactions' },
              { title: 'New Payment', active: true },
            ],
          },
        },
        {
          path: '/payment-transactions/:id',
          name: 'payment-transaction-detail',
          component: () =>
            import(
              './views/apps/payment-transactions/PaymentTransactionForm.vue'
            ),
          meta: {
            pageTitle: 'Display Payment',
            rule: 'admin',
            authRequired: true,
            breadcrumb: [
              { title: 'Dashboard', url: '/dashboard' },
              { title: 'Payments', url: '/payment-transactions' },
              { title: 'Display', active: true },
            ],
          },
        },
        {
          path: '/setting',
          name: 'setting',
          component: () => import('./views/apps/setting/Setting.vue'),
          meta: {
            pageTitle: 'Company Settings',
            i18nKey: 'nav.companySettings',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/smart-pos',
          name: 'smart-pos',
          component: () => import('./views/apps/external/SmartPos.vue'),
          meta: {
            pageTitle: 'SmartPos',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/setting/:type',
          name: 'setting-detail',
          component: () =>
            import('./views/apps/setting/PaymentSettingForm.vue'),
          meta: {
            pageTitle: 'Setting Detail',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/pdf-templates',
          name: 'pdf-templates',
          component: () =>
            import('./views/apps/setting/PdfTemplateSetting.vue'),
          meta: {
            pageTitle: 'PDF Templates',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/roles',
          name: 'roles',
          component: () =>
            import('./views/apps/setting/access/RoleSetting.vue'),
          meta: {
            pageTitle: 'Roles',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/roles/:id',
          name: 'role-detail',
          component: () =>
            import('./views/apps/setting/access/RoleSettingForm.vue'),
          meta: {
            pageTitle: 'Role Detail',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/users',
          name: 'users',
          component: () => import('./views/apps/setting/access/UsersList.vue'),
          meta: {
            pageTitle: 'Users',
            rule: 'admin',
            authRequired: true,
          },
        },
        {
          path: '/profile',
          name: 'profile',
          component: () => import('@/views/pages/Profile.vue'),
          meta: {
            authRequired: true,
            rule: 'admin',
            pageTitle: 'Profile',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Profile', active: true },
            ],
          },
        },
        {
          path: '/faq',
          name: 'faq',
          component: () => import('@/views/apps/about-us/Faq.vue'),
          meta: {
            authRequired: true,
            rule: 'admin',
            pageTitle: 'FAQ',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'FAQ', active: true },
            ],
          },
        },
        {
          path: '/about-us',
          name: 'about-us',
          component: () => import('@/views/apps/about-us/AboutUs.vue'),
          meta: {
            authRequired: true,
            rule: 'admin',
            pageTitle: 'About Us',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'About Us', active: true },
            ],
          },
        },
        {
          path: '/privacy-policy',
          name: 'privacy-policy',
          component: () => import('@/views/apps/about-us/PrivacyPolicy.vue'),
          meta: {
            authRequired: true,
            rule: 'admin',
            pageTitle: 'Privacy Policy',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Privacy Policy', active: true },
            ],
          },
        },
        {
          path: '/terms-and-conditions',
          name: 'terms-and-conditions',
          component: () =>
            import('@/views/apps/about-us/TermsAndConditions.vue'),
          meta: {
            authRequired: true,
            rule: 'admin',
            pageTitle: 'Terms and Conditions',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Terms and Conditions', active: true },
            ],
          },
        },
        // =============================================================================
        // Application Routes
        // NOTE: Our Pages start here
        // =============================================================================
      ],
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        {
          path: '/reset-password',
          name: 'reset-password',
          component: () => import('./views/pages/ResetPassword.vue'),
          meta: {
            pageTitle: 'Reset Password',
            authRequired: false,
            rule: 'public',
          },
        },
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/callback',
          name: 'auth-callback',
          component: () => import('@/views/Callback.vue'),
          meta: {
            rule: 'public',
          },
        },
        {
          path: '/login',
          name: 'page-login',
          component: () => import('@/views/pages/login/Login.vue'),
          meta: {
            rule: 'public',
          },
        },
        {
          path: '/select-tenant',
          name: 'page-select-tenant',
          component: () => import('@/views/pages/login/SelectTenant.vue'),
          meta: {
            rule: 'public',
          },
        },
        {
          path: '/forgot-password',
          name: 'page-forgot-password',
          component: () => import('@/views/pages/ForgotPassword.vue'),
          meta: {
            rule: 'public',
          },
        },
        {
          path: '/forgot-password-message',
          name: 'page-forgot-password-message',
          component: () => import('@/views/pages/ForgotPasswordMessage.vue'),
          meta: {
            rule: 'public',
          },
        },
        {
          path: '/reset-password',
          name: 'page-reset-password',
          component: () => import('@/views/pages/ResetPassword.vue'),
          meta: {
            rule: 'public',
          },
        },
        {
          path: '/404',
          name: 'page-error-404',
          component: () => import('@/views/pages/Error404.vue'),
          meta: {
            rule: 'public',
          },
        },
        {
          path: '/pages/error-500',
          name: 'page-error-500',
          component: () => import('@/views/pages/Error500.vue'),
          meta: {
            rule: 'public',
          },
        },
        {
          path: '/pages/not-authorized',
          name: 'page-not-authorized',
          component: () => import('@/views/pages/NotAuthorized.vue'),
          meta: {
            rule: 'public',
          },
        },
        {
          path: '/pages/maintenance',
          name: 'page-maintenance',
          component: () => import('@/views/pages/Maintenance.vue'),
          meta: {
            rule: 'public',
          },
        },
        {
          path: '/pages/upload',
          name: 'upload',
          component: () => import('@/views/pages/Upload.vue'),
          meta: {
            rule: 'public',
          },
        },
      ],
    },

    // Redirect to 404 page, if no match found
    {
      path: '*',
      component: () => import('@/views/pages/Error404.vue'),
      meta: {
        rule: 'public',
      },
    },
  ],
})

router.afterEach((to) => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
  if (to.meta && to.meta.pageTitle) {
    document.title = to.meta.pageTitle;
  }
})

router.beforeEach(async (to, from, next) => {
  const permissions = JSON.parse(localStorage.getItem('permissions'))


  if (to.meta && to.meta.authRequired) {
    if (to.meta.permission) {
      const permissionsFilter = permissions.filter((permission) => {
        return permission.code === to.meta.permission
      })
      if (permissionsFilter.length === 0) {
        return next({ path: '/pages/not-authorized', query: { to: to.path } })
      }
    }
    if (!auth.isAuthenticated()) {
      return next({ path: '/login', query: { to: to.path } })
    }
  }
  return next()
})

export default router
